import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/api';
import { Team } from '../types';
import { 
  Container, 
  Card, 
  LoadingSpinner,
  colors,
  spacing,
  typography,
  borderRadius,
  media
} from '../styles/GlobalStyles';
import { 
  Users, 
  Star,
  Crown,
  Expand,
  Minimize
} from 'lucide-react';

const TeamsContainer = styled.div`
  min-height: calc(100vh - 80px);
  background: #000000;
`;

const PageHeader = styled.div`
  margin-bottom: ${spacing[8]};
  text-align: center;
`;

const PageTitle = styled.h1`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[2]};

  @media (max-width: 380px) {
    font-size: ${typography.fontSize['2xl']};
  }
`;

const PageSubtitle = styled.p`
  font-size: ${typography.fontSize.lg};
  color: ${colors.grey[300]};

  @media (max-width: 380px) {
    font-size: ${typography.fontSize.base};
    line-height: 1.4;
  }
`;

const TeamsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};

  @media (max-width: 380px) {
    gap: ${spacing[4]};
  }
`;

const TeamCard = styled(Card)<{ isUserTeam?: boolean; isExpanded?: boolean }>`
  background: #060606 !important;
  border: 1px solid #252525;
  border-radius: 0.25rem;
  overflow: hidden;
`;

const TeamHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem;
  background: #202021;
  border-bottom: 1px solid #252525;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${colors.grey[800]};
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    gap: ${spacing[4]};
    padding: ${spacing[4]};
  }

  @media (max-width: 380px) {
    gap: ${spacing[3]};
    padding: ${spacing[3]};
  }
`;

const TeamMainInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[4]};
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
  }

  @media (max-width: 380px) {
    gap: ${spacing[3]};
  }
`;

const TeamAvatar = styled.div<{ isUserTeam?: boolean }>`
  width: 60px;
  height: 60px;
  background: ${props => props.isUserTeam ? colors.green : colors.grey[600]};
  color: ${colors.black};
  border-radius: ${borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${typography.fontWeight.bold};
  font-size: ${typography.fontSize.xl};
  position: relative;
  
  ${props => props.isUserTeam && `
    &::after {
      content: '';
      position: absolute;
      top: -2px;
      right: -2px;
      width: 20px;
      height: 20px;
      background: ${colors.green};
      border-radius: ${borderRadius.full};
      border: 2px solid ${colors.black};
    }
  `}

  @media (max-width: 768px) {
    width: 52px;
    height: 52px;
    font-size: ${typography.fontSize.lg};
    flex-shrink: 0;
  }

  @media (max-width: 380px) {
    width: 46px;
    height: 46px;
    font-size: ${typography.fontSize.base};

    &::after {
      width: 16px;
      height: 16px;
    }
  }
`;

const TeamDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

const TeamName = styled.h3`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin: 0 0 ${spacing[1]} 0;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  line-height: 1.2;
  word-break: break-word;

  @media (max-width: 768px) {
    font-size: ${typography.fontSize.lg};
  }

  @media (max-width: 380px) {
    font-size: ${typography.fontSize.base};
  }
`;

const TeamNumber = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};

  @media (max-width: 380px) {
    font-size: ${typography.fontSize.xs};
  }
`;

const OwnerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-top: ${spacing[2]};
`;

const OwnerAvatar = styled.div`
  width: 20px;
  height: 20px;
  background: ${colors.grey[500]};
  color: ${colors.black};
  border-radius: ${borderRadius.full};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${typography.fontWeight.semibold};
  font-size: ${typography.fontSize.xs};
`;

const OwnerName = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[300]};
  word-break: break-word;

  @media (max-width: 380px) {
    font-size: ${typography.fontSize.xs};
  }
`;

const TeamStats = styled.div`
  display: flex;
  gap: ${spacing[6]};
  align-items: center;

  @media (max-width: 768px) {
    width: 100%;
    gap: ${spacing[3]};
    justify-content: space-between;
  }

  @media (max-width: 380px) {
    gap: ${spacing[2]};
  }
`;

const StatItem = styled.div`
  text-align: center;
  min-width: 80px;

  @media (max-width: 768px) {
    min-width: 0;
    flex: 1;
  }
`;

const StatValue = styled.div`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};

  @media (max-width: 380px) {
    font-size: ${typography.fontSize.base};
  }
`;

const StatLabel = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[400]};
  text-transform: uppercase;

  @media (max-width: 380px) {
    font-size: 0.6875rem;
    letter-spacing: 0.3px;
  }
`;

const BudgetInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${spacing[2]};
  min-width: 120px;

  @media (max-width: 768px) {
    width: 100%;
    min-width: 0;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const BudgetAmount = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'remaining'
})<{ remaining: number }>`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${props => {
    if (props.remaining < 50) return colors.error;
    if (props.remaining < 100) return colors.warning;
    return colors.success;
  }};

  @media (max-width: 380px) {
    font-size: ${typography.fontSize.base};
  }
`;

const BudgetLabel = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[400]};
  text-transform: uppercase;
`;

const ExpandButton = styled.button`
  background: none;
  border: none;
  color: ${colors.grey[400]};
  cursor: pointer;
  padding: ${spacing[2]};
  border-radius: ${borderRadius.base};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  min-height: 44px;
  min-width: 44px;
  
  &:hover {
    background: ${colors.grey[700]};
    color: ${colors.white};
  }

  @media (max-width: 768px) {
    padding: ${spacing[2]} ${spacing[3]};
  }

  @media (max-width: 380px) {
    padding: ${spacing[2]};
  }

  @media (max-width: 420px) {
    span {
      display: none;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};

  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }

  @media (max-width: 380px) {
    gap: ${spacing[1]};
  }
`;

const TeamBadge = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'type'
})<{ type: 'user' | 'admin' }>`
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.base};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  
  ${props => props.type === 'user' ? `
    background: ${colors.green}20;
    color: ${colors.green};
  ` : `
    background: ${colors.info}20;
    color: ${colors.info};
  `}

  @media (max-width: 380px) {
    padding: 0.125rem ${spacing[2]};
    font-size: 0.6875rem;
  }
`;

const BudgetBar = styled.div`
  margin-bottom: ${spacing[4]};
  padding: 0 ${spacing[6]};
`;

const ExpandedContent = styled.div<{ isExpanded: boolean }>`
  max-height: ${props => props.isExpanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const ProgressBarContainer = styled.div`
  width: 100%;
  height: 8px;
  background: ${colors.grey[700]};
  border-radius: ${borderRadius.full};
  overflow: hidden;
  margin-top: ${spacing[2]};
`;

const ProgressBar = styled.div.withConfig({
  shouldForwardProp: (prop) => !['percentage', 'remaining'].includes(prop)
})<{ percentage: number; remaining: number }>`
  height: 100%;
  width: ${props => 100 - props.percentage}%;
  background: ${props => {
    if (props.remaining < 50) return colors.error;
    if (props.remaining < 100) return colors.warning;
    return colors.success;
  }};
  transition: width 0.3s ease;
`;

const PlayersSection = styled.div`
  padding: ${spacing[6]};

  @media (max-width: 768px) {
    padding: ${spacing[4]};
  }
`;

const PlayersHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${spacing[4]};
`;

const PlayersTitle = styled.h4`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin: 0;
`;

const PlayersCount = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
`;

const PositionGroups = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
`;

const PositionGroup = styled.div`
  background: ${colors.grey[800]};
  border: 1px solid ${colors.grey[700]};
  border-radius: ${borderRadius.base};
  overflow: hidden;
`;

const PositionHeader = styled.div`
  background: ${colors.grey[700]};
  padding: ${spacing[3]} ${spacing[4]};
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${colors.grey[600]};
`;

const PositionTitle = styled.h5`
  font-size: ${typography.fontSize.base};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PositionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
`;

const PositionCount = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
  background: ${colors.grey[600]};
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${borderRadius.base};
`;

const PositionCost = styled.span`
  font-size: ${typography.fontSize.sm};
  color: ${colors.green};
  font-weight: ${typography.fontWeight.semibold};
`;

const PlayersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${spacing[3]};
  padding: ${spacing[4]};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: ${spacing[3]};
  }

  ${media.tablet} {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }
`;

const PlayerCard = styled.div`
  background: ${colors.grey[700]};
  border: 1px solid ${colors.grey[600]};
  border-radius: ${borderRadius.base};
  padding: ${spacing[3]};
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 80px;
  
  &:hover {
    background: ${colors.grey[600]};
    border-color: ${colors.grey[500]};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
`;

const PlayerName = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
  line-height: 1.2;
`;

const PlayerDetails = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.grey[400]};
  margin-bottom: ${spacing[2]};
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
`;

const PlayerPosition = styled.span`
  background: ${colors.grey[600]};
  color: ${colors.white};
  padding: 2px 6px;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
`;

const PlayerTeam = styled.span`
  color: ${colors.grey[300]};
`;

const ByeWeek = styled.span`
  color: ${colors.grey[400]};
`;

const PlayerCost = styled.div`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.green};
  text-align: right;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing[8]};
  color: ${colors.grey[400]};
`;

const TeamsPage: React.FC = () => {
  const { user } = useAuth();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState<Set<string>>(new Set());
  const rosterSize = 20;

  useEffect(() => {
    loadTeams();
  }, []);

  const loadTeams = async () => {
    try {
      setLoading(true);
      const response = await apiClient.teams.getAll();
      if (response.success) {
        setTeams(response.data.teams);
      }
    } catch (error) {
      console.error('Failed to load teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const getUserTeam = () => {
    return teams.find(team => team.ownerId?._id === user?._id);
  };

  const toggleTeamExpansion = (teamId: string) => {
    const newExpanded = new Set(expandedTeams);
    if (newExpanded.has(teamId)) {
      newExpanded.delete(teamId);
    } else {
      newExpanded.add(teamId);
    }
    setExpandedTeams(newExpanded);
  };

  const groupPlayersByPosition = (players: any[]) => {
    const grouped = players.reduce((acc: { [key: string]: any[] }, player: any) => {
      const position = player.position || 'Unknown';
      if (!acc[position]) {
        acc[position] = [];
      }
      acc[position].push(player);
      return acc;
    }, {});

    // Sort positions in a logical order (QB, RB, WR, TE, K, DEF, etc.)
    const positionOrder = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF', 'DST', 'Unknown'];
    const sortedPositions = Object.keys(grouped).sort((a, b) => {
      const aIndex = positionOrder.indexOf(a);
      const bIndex = positionOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

    return sortedPositions.map(position => ({
      position,
      players: grouped[position].sort((a: any, b: any) => {
        // Sort players within each position by cost (descending), then by name
        if (a.cost !== b.cost) {
          return b.cost - a.cost;
        }
        return a.playerName.localeCompare(b.playerName);
      })
    }));
  };

  const getTeamPlayerCount = (team: Team) => {
    return Array.isArray(team.players) ? team.players.length : 0;
  };

  const getSlotsLeft = (team: Team) => {
    if (typeof team.slotsRemaining === 'number' && Number.isFinite(team.slotsRemaining)) {
      return team.slotsRemaining;
    }

    return Math.max(rosterSize - getTeamPlayerCount(team), 0);
  };

  if (loading) {
    return (
      <TeamsContainer>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <LoadingSpinner size="lg" />
          </div>
        </Container>
      </TeamsContainer>
    );
  }

  const userTeam = getUserTeam();

  return (
    <TeamsContainer>
      <Container>
        <PageHeader>
          <PageTitle>Fantasy Teams</PageTitle>
          <PageSubtitle>
            Track all teams, budgets, and rosters in the league
          </PageSubtitle>
        </PageHeader>

        {teams.length > 0 ? (
          <TeamsList>
            {teams.map(team => {
              const isUserTeam = userTeam?._id === team._id;
              const isExpanded = expandedTeams.has(team._id);
              const budgetPercentage = ((team.budget.total - team.budget.remaining) / team.budget.total) * 100;
              
              return (
                <TeamCard key={team._id} isUserTeam={isUserTeam} isExpanded={isExpanded}>
                  <TeamHeader onClick={() => toggleTeamExpansion(team._id)}>
                    <TeamMainInfo>
                      <TeamAvatar isUserTeam={isUserTeam}>
                        {team.name.charAt(0).toUpperCase()}
                      </TeamAvatar>
                      
                      <TeamDetails>
                        <TeamName>
                          {team.name}
                          {isUserTeam && <Crown size={16} />}
                        </TeamName>
                        <TeamNumber>Team #{team.teamNumber}</TeamNumber>
                        
                        {team.ownerId && (
                          <OwnerInfo>
                            <OwnerAvatar>
                              {team.ownerId.username.charAt(0).toUpperCase()}
                            </OwnerAvatar>
                            <OwnerName>{team.ownerId.username}</OwnerName>
                          </OwnerInfo>
                        )}
                      </TeamDetails>
                    </TeamMainInfo>
                    
                    <TeamStats>
                      <StatItem>
                        <StatValue>{getTeamPlayerCount(team)}</StatValue>
                        <StatLabel>Players</StatLabel>
                      </StatItem>
                      
                      <StatItem>
                        <StatValue>${team.budget.spent}</StatValue>
                        <StatLabel>Spent</StatLabel>
                      </StatItem>
                      
                      <StatItem>
                        <StatValue>{getSlotsLeft(team)}</StatValue>
                        <StatLabel>Slots Left</StatLabel>
                      </StatItem>
                    </TeamStats>

                    <BudgetInfo>
                      <BudgetAmount remaining={team.budget.remaining}>
                        ${team.budget.remaining}
                      </BudgetAmount>
                      <BudgetLabel>Remaining</BudgetLabel>
                    </BudgetInfo>

                    <HeaderActions>
                      {isUserTeam && (
                        <TeamBadge type="user">
                          <Star size={12} />
                          Your Team
                        </TeamBadge>
                      )}
                      
                      <ExpandButton>
                        {isExpanded ? (
                          <>
                            <Minimize size={16} />
                            <span>Collapse</span>
                          </>
                        ) : (
                          <>
                            <Expand size={16} />
                            <span>Expand</span>
                          </>
                        )}
                      </ExpandButton>
                    </HeaderActions>
                  </TeamHeader>

                  <ExpandedContent isExpanded={isExpanded}>
                    <BudgetBar>
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: spacing[2]
                      }}>
                        <span style={{ fontSize: typography.fontSize.sm, color: colors.grey[300] }}>
                          Budget Usage
                        </span>
                        <span style={{ fontSize: typography.fontSize.sm, color: colors.grey[400] }}>
                          {budgetPercentage.toFixed(1)}% used
                        </span>
                      </div>
                      
                      <ProgressBarContainer>
                        <ProgressBar 
                          percentage={budgetPercentage} 
                          remaining={team.budget.remaining}
                        />
                      </ProgressBarContainer>
                    </BudgetBar>

                    <PlayersSection>
                    <PlayersHeader>
                      <PlayersTitle>Roster</PlayersTitle>
                      <PlayersCount>{getTeamPlayerCount(team)} players</PlayersCount>
                    </PlayersHeader>
                    
                    {getTeamPlayerCount(team) > 0 ? (
                      <PositionGroups>
                        {groupPlayersByPosition(team.players).map(({ position, players }) => {
                          const totalCost = players.reduce((sum: number, player: any) => sum + player.cost, 0);
                          return (
                            <PositionGroup key={position}>
                              <PositionHeader>
                                <PositionTitle>{position}</PositionTitle>
                                <PositionInfo>
                                  <PositionCost>${totalCost}</PositionCost>
                                  <PositionCount>{players.length}</PositionCount>
                                </PositionInfo>
                              </PositionHeader>
                              <PlayersGrid>
                                {players.map((player: any, index: number) => (
                                  <PlayerCard key={index}>
                                    <div>
                                      <PlayerName>{player.playerName}</PlayerName>
                                      <PlayerDetails>
                                        <PlayerPosition>{player.position}</PlayerPosition>
                                        <PlayerTeam>{player.team}</PlayerTeam>
                                        <ByeWeek>{`• Bye ${player.byeWeek ?? '--'}`}</ByeWeek>
                                      </PlayerDetails>
                                    </div>
                                    <PlayerCost>${player.cost}</PlayerCost>
                                  </PlayerCard>
                                ))}
                              </PlayersGrid>
                            </PositionGroup>
                          );
                        })}
                      </PositionGroups>
                    ) : (
                      <div style={{ 
                        textAlign: 'center', 
                        padding: spacing[6],
                        color: colors.grey[400],
                        fontSize: typography.fontSize.sm,
                        background: colors.grey[800],
                        borderRadius: borderRadius.base
                      }}>
                        No players drafted yet
                      </div>
                                          )}
                    </PlayersSection>
                  </ExpandedContent>
                </TeamCard>
              );
            })}
          </TeamsList>
        ) : (
          <EmptyState>
            <Users size={64} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
            <h3 style={{ color: colors.white, marginBottom: spacing[2] }}>
              No Teams Yet
            </h3>
            <p>
              {user?.role === 'admin' 
                ? 'Create your first team to get started'
                : 'Wait for an admin to create teams'
              }
            </p>
          </EmptyState>
        )}
      </Container>
    </TeamsContainer>
  );
};

export default TeamsPage;
